import React, { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import logoMark from '../assets/logo-mark.png';
import { usePageHref } from '../api/pages';
import PageLink from './PageLink';

export const SUPPORT_EMAIL = 'support@rentagade.com';

interface PublicLayoutProps {
  /** Browser tab title; " · Rentagade" is appended. */
  title: string;
  children: React.ReactNode;
  /** Hide The App / FAQ / Privacy in the header (the policy pages show just the logo). */
  hideNav?: boolean;
}


/**
 * Shell for the public, unauthenticated pages (app landing, FAQ, privacy
 * policy). These URLs are submitted to the App Store / Play Store, so they
 * must render without a login.
 */
const PublicLayout: React.FC<PublicLayoutProps> = ({ title, children, hideNav = false }) => {
  const privacy = usePageHref('privacy-policy');
  const NAV = [
    { to: '/', label: 'The App' },
    { to: '/faq', label: 'FAQ' },
    { to: privacy.href, label: 'Privacy', external: privacy.external },
  ];
  const navClass = (isActive: boolean) =>
    `px-2 sm:px-3 py-1.5 rounded-lg text-sm whitespace-nowrap font-semibold transition-colors ${
      isActive ? 'text-brand bg-brand-bg' : 'text-g600 hover:text-ink'
    }`;

  useEffect(() => {
    document.title = `${title} · Rentagade`;
    window.scrollTo(0, 0);
    return () => {
      document.title = 'Rentagade';
    };
  }, [title]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--canvas)' }}>
      <header className="bg-white border-b border-g200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 no-underline">
            <img
              src={logoMark}
              alt="Rentagade"
              className="rounded-full object-contain"
              style={{ width: 36, height: 36 }}
            />
            <span className="hidden min-[400px]:inline text-lg font-bold text-ink">Rentagade</span>
          </Link>
          {!hideNav && (
            <nav className="flex items-center gap-1 sm:gap-2">
              {NAV.map((n) =>
                n.external ? (
                  <a key={n.to} href={n.to} rel="noopener noreferrer" className={navClass(false)}>
                    {n.label}
                  </a>
                ) : (
                  <NavLink key={n.to} to={n.to} end className={({ isActive }) => navClass(isActive)}>
                    {n.label}
                  </NavLink>
                )
              )}
            </nav>
          )}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-white border-t border-g200 mt-12">
        <div className="max-w-5xl mx-auto px-4 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-g500">
          <div>© {new Date().getFullYear()} Rentagade. All rights reserved.</div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/" className="hover:text-ink">
              The App
            </Link>
            <Link to="/faq" className="hover:text-ink">
              FAQ
            </Link>
            <PageLink slug="privacy-policy" className="hover:text-ink">
              Privacy Policy
            </PageLink>
            <PageLink slug="terms-of-service" className="hover:text-ink">
              Terms of Service
            </PageLink>
            <PageLink slug="delete-account" className="hover:text-ink">
              Delete Account
            </PageLink>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-ink">
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
